/**
 * Step 2 — what the numbers mean (cashflow rebuild, 21 Oct 2026; the agreed mock-up's "Why the Age Pension is
 * what it is", with the cashflow and the estate beside it).
 *
 *  - WHY THE AGE PENSION IS WHAT IT IS (owner ruling, every module): which test sets it, what Centrelink
 *    counts, what every $1,000 moved out of the count is worth, and how much would bring the full pension.
 *    Read from the engine's own breakdown; Centrelink's working is one fold away.
 *  - THE MONEY EACH YEAR: money in, money out, line by line; and where a shortfall is met from, account by
 *    account and in the engine's order (the money story every module's Explain this tells).
 *  - WHAT THEY OWN AND LEAVE: today's net worth, line by line, and how the estate figure is reached.
 *
 * One source, as before: the pension and cashflow are the first projected year WITH the support cost; what
 * they own today is the run WITHOUT it (paying for support next year cannot change what they own now).
 */
import React from 'react';
import { ArrowRight, ChevronLeft, ChevronDown } from 'lucide-react';
import { householdVoice } from '../../utils/householdWords.ts';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';
import { MeansTestBeats } from '../../components/insights/MeansTestPosition.tsx';
import { planRunwayCopy, summariseFunding } from '../../components/insights/fundingSummary.tsx';
import { buildMoneyStory, MoneyStoryBlock } from '../agedCare/moneyStory.tsx';
import { RATES_PROVENANCE } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { CashflowYear, PositionSummary, SupportCostInput } from './cashflowPensionScenario.ts';
import { cpPensionWhyOf, cpPensionWhyWords } from './cpPlanWords.ts';
import { BreakdownList, card, sec, money, cashWords, link } from './cpUi.tsx';

export interface CpStep2Props {
    factFind: DraftFactFind;
    /** The first projected year, with the support cost. */
    first: CashflowYear | undefined;
    /** The first projected year without it — what they own today. */
    todayFirst: CashflowYear | undefined;
    /** Every projected year, changing nothing, with the support cost. */
    years: CashflowYear[] | null;
    position: PositionSummary | null;
    supportCost: SupportCostInput | null;
    legacyYears: number;
    onBack: () => void;
    onNext: () => void;
    onAssumptions: () => void;
}

const Fold = ({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) => {
    const [open, setOpen] = React.useState(false);
    return (
        <div className="mt-4 border-t border-slate-200 pt-3">
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 hover:text-blue-900" data-testid={testId}>
                <ChevronDown size={15} className={`transition-transform ${open ? 'rotate-180' : ''}`} /> {open ? 'Hide' : label}
            </button>
            {open && <div className="mt-3 text-[13px] leading-relaxed text-slate-600 md:columns-2 md:gap-8">{children}</div>}
        </div>
    );
};

const CpStep2 = ({ factFind, first, todayFirst, years, position, supportCost, legacyYears, onBack, onNext, onAssumptions }: CpStep2Props) => {
    const v = householdVoice(factFind);
    const isCouple = factFind?.adviceFor === 'couple';
    const why = cpPensionWhyOf(first, isCouple);
    const rows = years || [];
    const story = rows.length && summariseFunding(rows as never).hasDeficit
        ? buildMoneyStory({ rows: rows as never, factFind, runway: planRunwayCopy(rows as never, rows.length), deposit: { paid: 0, refundTo: '' }, horizonYears: legacyYears, enteringName: factFind?.client?.firstName || 'The household', spentOn: 'the plan' })
        : null;
    if (!position) {
        return <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-[14px] text-slate-600">Working out {v.possessive} position…</div>;
    }
    return (
        <div className="space-y-5" data-testid="cp-step2">
            <div className={card} data-testid="cp-why-pension">
                <p className={sec}>Why {v.possessive} Age Pension is {money(position.agePension)} a year</p>
                {why ? (
                    <p className="mt-2 text-[16px] leading-relaxed text-slate-800" data-testid="cp-why-pension-words">{cpPensionWhyWords(why, v.their)}</p>
                ) : (
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-700">Centrelink&apos;s working isn&apos;t available on {v.possessive} figures — usually because no one on the file is of Age Pension age yet.</p>
                )}
                <PlainWordsCard eyebrow={`Where ${v.names} stand${v.s} today`} className="mt-4 border-0 bg-transparent p-0 shadow-none" collapsible>
                    <MeansTestBeats careDepositRelevant={Boolean(factFind?.goals?.aged_care)} financials={first as { socialSecurity?: number; socialSecurityBreakdown?: unknown } | undefined} isCouple={isCouple} name={v.names} whereNext="The plans on the next step" />
                </PlainWordsCard>
                <Fold label="See Centrelink's working" testId="cp-pension-working">
                    <BreakdownList rows={first?.socialSecurityBreakdown} />
                    <p className="mt-3 border-t border-slate-200 pt-3 text-slate-500">Centrelink works out both tests and pays whichever gives the <strong>lower</strong> amount.</p>
                </Fold>
            </div>

            <div className={card} data-testid="cp-money-each-year">
                <p className={sec}>{v.Possessive} money each year</p>
                <p className="mt-2 text-[16px] leading-relaxed text-slate-800">
                    Money in, less money out, over the next twelve months: <strong className={position.cashFlow < 0 ? 'text-rose-700' : 'text-emerald-700'}>{cashWords(position.cashFlow)}</strong>
                    {supportCost ? <>, after {money(supportCost.annual)} a year of {supportCost.label.toLowerCase()}</> : null}.
                    {position.cashFlow < 0 && <> The gap is met from savings. That is not automatically wrong — it is what savings are for — but it is worth seeing where it comes from.</>}
                </p>
                {story && (
                    <div className="mt-3 text-[15px] leading-relaxed text-slate-700" data-testid="cp-step2-money-story">
                        <p className="text-[13px] font-semibold text-slate-600">Where the gap comes from</p>
                        <div className="mt-1"><MoneyStoryBlock story={story} testId="cp-money-story" /></div>
                    </div>
                )}
                <Fold label="See every line, money in and money out" testId="cp-cashflow-working">
                    <BreakdownList rows={first?.cashFlowBreakdown} />
                </Fold>
            </div>

            <div className={card} data-testid="cp-own-and-leave">
                <p className={sec}>What {v.they} own, and what is left to the family</p>
                <p className="mt-2 text-[16px] leading-relaxed text-slate-800">
                    {v.Names} own{v.s} <strong>{money(position.netWorth)}</strong> today, less what {v.they} owe. Changing nothing, about <strong>{money(position.estate)}</strong> is left to the family after about {legacyYears} years, after living costs, investment returns{supportCost ? ' and the support paid for' : ''}.
                </p>
                <Fold label={`See what makes up what ${v.they} own`} testId="cp-networth-working">
                    <BreakdownList rows={todayFirst?.netWorthBreakdown} />
                </Fold>
                <Fold label="See how the estate figure is reached" testId="cp-estate-working">
                    <BreakdownList
                        rows={[
                            { label: 'Net worth today', value: position.netWorth },
                            { label: 'Change over the period', value: position.estate - position.netWorth },
                            { label: `Left to the family after ${legacyYears} years`, value: position.estate, emphasis: true },
                        ]}
                    />
                </Fold>
            </div>

            <p className="text-[13px] text-slate-500">
                Figures use the rates in force from {RATES_PROVENANCE.agePension}. <button type="button" onClick={onAssumptions} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2">What the figures assume</button>.
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={onBack} className={`inline-flex items-center gap-1 text-[14px] ${link}`}><ChevronLeft size={15} /> Change the support cost</button>
                <button type="button" onClick={onNext} data-testid="cp-step2-next"
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-[14px] font-semibold text-white shadow-sm ">
                    See ways to improve it <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default CpStep2;
